import {FC, useLayoutEffect, useRef, useState} from 'react'
import {useTranslation} from '@/translations/i18n.tsx'
import {Badge} from '@components/ui/badge/Badge.tsx'
import {Tooltip} from '@components/ui/tooltip/Tooltip.tsx'
import {Flexbox} from '@components/ui/flexbox/FlexBox.tsx'
import {TableBodyCellText} from '@components/ui/table-atoms/TableAtoms.tsx'
import {UserWorkArea} from '@/features/users/types.ts'
import {StyledOverflowBadgeTrigger, StyledWorkAreasMeasureRow, StyledWorkAreasWrapper} from './style.ts'

interface WorkAreasBadgesProps {
    workAreas: UserWorkArea[]
}

/* Renders the work areas as a single row of badges that never wraps: it measures the badges against the
available width and folds the ones that do not fit into a "+N" badge, whose tooltip lists the folded ones. */
export const WorkAreasBadges: FC<WorkAreasBadgesProps> = ({workAreas}) => {
    const {t} = useTranslation()
    const wrapperRef = useRef<HTMLDivElement>(null)
    const measureRowRef = useRef<HTMLDivElement>(null)
    const [visibleCount, setVisibleCount] = useState(workAreas.length)

    useLayoutEffect(() => {
        const wrapper = wrapperRef.current
        const measureRow = measureRowRef.current
        if (!wrapper || !measureRow) {
            return
        }

        const compute = () => {
            const available = wrapper.clientWidth
            const gap = parseFloat(getComputedStyle(measureRow).columnGap) || 0
            const [overflowBadge, ...badges] = Array.from(measureRow.children) as HTMLElement[]
            const widths = badges.map(badge => badge.offsetWidth)
            // Width of the first `count` badges including the gaps between them
            const rowWidth = (count: number) =>
                widths.slice(0, count).reduce((sum, width) => sum + width, 0) + Math.max(0, count - 1) * gap

            if (rowWidth(widths.length) <= available) {
                setVisibleCount(widths.length)
                return
            }

            // Some badges overflow: keep the longest prefix that still leaves room for the "+N" badge
            const overflowWidth = overflowBadge?.offsetWidth ?? 0
            let count = widths.length - 1
            while (count > 0 && rowWidth(count) + gap + overflowWidth > available) {
                count--
            }
            setVisibleCount(count)
        }

        compute()
        const observer = new ResizeObserver(compute)
        observer.observe(wrapper)
        return () => observer.disconnect()
    }, [workAreas])

    if (workAreas.length == 0) {
        return <TableBodyCellText variant="light">{t('commons:none')}</TableBodyCellText>
    }

    const hiddenCount = workAreas.length - visibleCount

    return (
        <StyledWorkAreasWrapper ref={wrapperRef}>
            {/* The "+N" badge comes first so the measurement can single it out; it shows the largest possible count */}
            <StyledWorkAreasMeasureRow ref={measureRowRef} aria-hidden>
                <Badge shape="rectangle" size="sm">
                    +{workAreas.length}
                </Badge>
                {workAreas.map(workArea => (
                    <Badge key={workArea.id} shape="rectangle" size="sm">
                        {workArea.area.name}
                    </Badge>
                ))}
            </StyledWorkAreasMeasureRow>
            {workAreas.slice(0, visibleCount).map(workArea => (
                <Badge key={workArea.id} shape="rectangle" size="sm">
                    {workArea.area.name}
                </Badge>
            ))}
            {hiddenCount > 0 && (
                <Tooltip
                    variant="light"
                    triggerProps={{asChild: true}}
                    content={
                        // Only the areas folded into the "+N" badge: the visible ones are already on the row
                        <Flexbox direction="column" gap={1}>
                            {workAreas.slice(visibleCount).map(workArea => (
                                <span key={workArea.id}>
                                    {workArea.area.name}
                                    {workArea.areaType == 'neighborhood' && ` · ${t('commons:neighbourhood')}`}
                                </span>
                            ))}
                        </Flexbox>
                    }
                >
                    <StyledOverflowBadgeTrigger>
                        <Badge shape="rectangle" size="sm">
                            +{hiddenCount}
                        </Badge>
                    </StyledOverflowBadgeTrigger>
                </Tooltip>
            )}
        </StyledWorkAreasWrapper>
    )
}
